import React from 'react';
import { FormSectionProps } from '@/lib/formTypes';

interface QuestionProps {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
  detailsValue?: string;
  detailsLabel?: string;
  onDetailsChange?: (value: string) => void;
  error?: string;
  detailsError?: string;
}

const YesNoQuestion: React.FC<QuestionProps> = ({
  label,
  value,
  onChange,
  detailsValue,
  detailsLabel,
  onDetailsChange,
  error,
  detailsError,
}) => (
  <div className="space-y-3">
    <div className="flex items-center justify-end gap-6">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="radio"
          name={label}
          checked={value === false}
          onChange={() => onChange(false)}
          className="w-4 h-4 accent-[#C17A4A]"
        />
        <span className="text-sm text-[#1B4332]">לא</span>
      </label>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="radio"
          name={label}
          checked={value === true}
          onChange={() => onChange(true)}
          className="w-4 h-4 accent-[#C17A4A]"
        />
        <span className="text-sm text-[#1B4332]">כן</span>
      </label>
      <span className="text-sm font-medium text-[#1B4332] text-right">{label}</span>
    </div>
    {error && <p className="text-red-500 text-sm text-right">{error}</p>}

    {value && detailsLabel && onDetailsChange && (
      <div className="mr-6 mt-3">
        <label className="block text-right mb-2">
          <span className="text-sm font-medium text-[#1B4332]">{detailsLabel}</span>
        </label>
        <textarea
          value={detailsValue || ''}
          onChange={(e) => onDetailsChange(e.target.value)}
          className={`w-full px-4 py-2 rounded-lg border-2 text-right focus:outline-none transition-colors ${
            detailsError
              ? 'border-red-500 focus:border-red-600'
              : 'border-[#E8A87C] focus:border-[#C17A4A]'
          }`}
          rows={3}
          placeholder="אנא פרט..."
        />
        {detailsError && (
          <p className="text-red-500 text-sm mt-1 text-right">{detailsError}</p>
        )}
      </div>
    )}
  </div>
);

export const MedicalQuestionnaire: React.FC<FormSectionProps> = ({
  data,
  errors,
  onChange,
}) => {
  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-xl font-bold text-[#1B4332] mb-6 text-right">
          חלק ב': שאלון רפואי
        </h3>
      </div>

      {/* Physical Limitation */}
      <YesNoQuestion
        label="האם קיימת מגבלה לבצוע פעילות גופנית?"
        value={data.physicalLimitation || false}
        onChange={(val) => onChange('physicalLimitation', val)}
        detailsValue={data.physicalLimitationDetails}
        detailsLabel="אנא פרט את המגבלה:"
        onDetailsChange={(val) => onChange('physicalLimitationDetails', val)}
        error={errors.physicalLimitation}
        detailsError={errors.physicalLimitationDetails}
      />

      {/* Blood Pressure */}
      <div className="space-y-3">
        <div className="flex items-center justify-end gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="bloodPressure"
              value="normal"
              checked={data.bloodPressure === 'normal'}
              onChange={(e) => onChange('bloodPressure', e.target.value)}
              className="w-4 h-4 accent-[#C17A4A]"
            />
            <span className="text-sm text-[#1B4332]">תקין</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="bloodPressure"
              value="low"
              checked={data.bloodPressure === 'low'}
              onChange={(e) => onChange('bloodPressure', e.target.value)}
              className="w-4 h-4 accent-[#C17A4A]"
            />
            <span className="text-sm text-[#1B4332]">נמוך</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="bloodPressure"
              value="high"
              checked={data.bloodPressure === 'high'}
              onChange={(e) => onChange('bloodPressure', e.target.value)}
              className="w-4 h-4 accent-[#C17A4A]"
            />
            <span className="text-sm text-[#1B4332]">גבוה</span>
          </label>
          <span className="text-sm font-medium text-[#1B4332] text-right">
            האם יש לך לחץ דם גבוה או נמוך?
          </span>
        </div>
        {errors.bloodPressure && (
          <p className="text-red-500 text-sm text-right">{errors.bloodPressure}</p>
        )}

        {data.bloodPressure && data.bloodPressure !== 'normal' && (
          <div className="mr-6 mt-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={data.bloodPressureManaged || false}
                onChange={(e) => onChange('bloodPressureManaged', e.target.checked)}
                className="w-4 h-4 accent-[#C17A4A]"
              />
              <span className="text-sm text-[#1B4332]">מאוזן תרופתית</span>
            </label>
          </div>
        )}
      </div>

      {/* Surgeries */}
      <YesNoQuestion
        label="האם עברת ניתוחים?"
        value={data.surgeries || false}
        onChange={(val) => onChange('surgeries', val)}
        detailsValue={data.surgeriesDetails}
        detailsLabel="אנא פרט את הניתוחים:"
        onDetailsChange={(val) => onChange('surgeriesDetails', val)}
        error={errors.surgeries}
        detailsError={errors.surgeriesDetails}
      />

      {/* Heart Events */}
      <YesNoQuestion
        label="האם היו אירועי לב?"
        value={data.heartEvents || false}
        onChange={(val) => onChange('heartEvents', val)}
        detailsValue={data.heartEventsDetails}
        detailsLabel="אנא פרט:"
        onDetailsChange={(val) => onChange('heartEventsDetails', val)}
        error={errors.heartEvents}
        detailsError={errors.heartEventsDetails}
      />

      {/* Injuries */}
      <YesNoQuestion
        label="האם עברת פציעה או תאונה?"
        value={data.injuries || false}
        onChange={(val) => onChange('injuries', val)}
        detailsValue={data.injuriesDetails}
        detailsLabel="אנא פרט:"
        onDetailsChange={(val) => onChange('injuriesDetails', val)}
        error={errors.injuries}
        detailsError={errors.injuriesDetails}
      />

      {/* Osteoporosis */}
      <YesNoQuestion
        label="האם את/ה סובל ממחלת דלדול עצם?"
        value={data.osteoporosis || false}
        onChange={(val) => onChange('osteoporosis', val)}
        error={errors.osteoporosis}
      />

      {/* Arthritis */}
      <div className="space-y-3">
        <div className="flex items-center justify-end gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="arthritis"
              checked={data.arthritis === false}
              onChange={() => onChange('arthritis', false)}
              className="w-4 h-4 accent-[#C17A4A]"
            />
            <span className="text-sm text-[#1B4332]">לא</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="arthritis"
              checked={data.arthritis === true}
              onChange={() => onChange('arthritis', true)}
              className="w-4 h-4 accent-[#C17A4A]"
            />
            <span className="text-sm text-[#1B4332]">כן</span>
          </label>
          <span className="text-sm font-medium text-[#1B4332] text-right">
            האם את/ה סובל מסכרת?
          </span>
        </div>
        {errors.arthritis && (
          <p className="text-red-500 text-sm text-right">{errors.arthritis}</p>
        )}

        {data.arthritis && (
          <div className="mr-6 mt-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={data.arthritisManaged || false}
                onChange={(e) => onChange('arthritisManaged', e.target.checked)}
                className="w-4 h-4 accent-[#C17A4A]"
              />
              <span className="text-sm text-[#1B4332]">מאוזנת תרופתית</span>
            </label>
          </div>
        )}
      </div>

      {/* Medications */}
      <YesNoQuestion
        label="נטילת תרופות באופן קבוע?"
        value={data.medications || false}
        onChange={(val) => onChange('medications', val)}
        detailsValue={data.medicationsDetails}
        detailsLabel="אנא פרט את התרופות:"
        onDetailsChange={(val) => onChange('medicationsDetails', val)}
        error={errors.medications}
        detailsError={errors.medicationsDetails}
      />

      {/* Smoking */}
      <YesNoQuestion
        label="האם את/ה מעשן/ת?"
        value={data.smoking || false}
        onChange={(val) => onChange('smoking', val)}
        detailsValue={data.smokingDetails}
        detailsLabel="אם כן, כמה ביום?"
        onDetailsChange={(val) => onChange('smokingDetails', val)}
        error={errors.smoking}
        detailsError={errors.smokingDetails}
      />

      {/* Pregnancy (for women) */}
      <YesNoQuestion
        label="האם את בהריון?"
        value={data.pregnancy || false}
        onChange={(val) => onChange('pregnancy', val)}
        detailsValue={data.pregnancyWeek}
        detailsLabel="אם כן, בשבוע כמה?"
        onDetailsChange={(val) => onChange('pregnancyWeek', val)}
        error={errors.pregnancy}
        detailsError={errors.pregnancyWeek}
      />

      {/* Additional Info */}
      <div>
        <label className="block text-right mb-2">
          <span className="text-sm font-medium text-[#1B4332]">
            מידע רפואי רלוונטי נוסף
          </span>
        </label>
        <textarea
          value={data.additionalInfo || ''}
          onChange={(e) => onChange('additionalInfo', e.target.value)}
          className={`w-full px-4 py-2 rounded-lg border-2 text-right focus:outline-none transition-colors ${
            errors.additionalInfo
              ? 'border-red-500 focus:border-red-600'
              : 'border-[#E8A87C] focus:border-[#C17A4A]'
          }`}
          rows={4}
          placeholder="אנא ציין כל מידע רפואי רלוונטי נוסף..."
        />
        {errors.additionalInfo && (
          <p className="text-red-500 text-sm mt-1 text-right">{errors.additionalInfo}</p>
        )}
      </div>
    </div>
  );
};
